<style lang="scss" scoped>
    .integration-tree {
        .btn-integration {
            min-width: 140px;

            &.active {
                border-color: #1976d2 !important;
                background-color: #E8EEF1 !important;
            }
        }

        /* Disable hover and pointer for leaf node */
        ::v-deep .v-treeview-node--leaf .v-treeview-node__root {
            cursor: default;
            &::before {
                display: none;
            }
        }

        ::v-deep .v-treeview-node__level {
            width: 12px;
        }
    }

    .no-selection {
        &-content {
            min-height: 300px;
        }
        .text-caption {
            max-width: 200px;
            line-height: 1.3;
        }
    }

    .list-wrap {
        border: thin solid rgba(0, 0, 0, 0.12);
        border-radius: 8px;

        .list-header {
            border-bottom: thin solid rgba(0, 0, 0, 0.12);
        }

        .list-header > span,
        .list-row > span {
            flex: 1 1;
            max-width: 160px;
        }
    }

    .integration-title-wrap {
        gap: 1.2rem;
    }
</style>
<template>
    <div dir="ltr">
        <v-row>
            <v-col cols="9">
                <p class="text-body-2">
                    {{ $t('partnerIntegrations.description') }}
                </p>
            </v-col>
        </v-row>
        <v-row>
            <v-col
                cols="3">
                <v-list
                    dense
                    flat
                    class="pt-0">
                    <v-treeview
                        v-if="integrationsTree"
                        class="text-body-2 integration-tree"
                        open-all
                        :items="integrationsTree"
                        :open.sync="openDoc"
                        item-key="id"
                        item-text="name"
                        item-children="children"
                        activatable
                        open-on-click
                        transition
                        @update:active="onItemClick">
                        <template #label="{ item }">
                            <!-- Leaf node as logo button -->
                            <div
                                v-if="!item.children || item.children.length === 0"
                                class="leaf-node">
                                <v-btn
                                    outlined
                                    x-large
                                    class="my-2 btn-integration"
                                    :class="{ 'active': item.id === activeLeafId }"
                                    @click.stop="onLeafClick(item)">
                                    <v-img
                                        :src="getLogoUrl(item.name)"
                                        :alt="item.name"
                                        width="44"
                                        contain />
                                </v-btn>
                            </div>

                            <!-- Parent node label -->
                            <span
                                v-else
                                class="text-caption text-uppercase">
                                {{ item.name }}
                            </span>
                        </template>
                    </v-treeview>

                    <v-treeview
                        v-if="orgIntegrations?.length"
                        class="text-body-2 integration-tree mt-2"
                        open-all
                        :items="orgIntegrationsTree"
                        :open.sync="openOrg"
                        item-key="id"
                        item-text="name"
                        item-children="children"
                        activatable
                        open-on-click
                        transition
                        @update:active="onItemClick">
                        <template #label="{ item }">
                            <!-- Leaf node as logo button -->
                            <div
                                v-if="!item.children || item.children.length === 0"
                                class="leaf-node">
                                <v-btn
                                    outlined
                                    x-large
                                    class="my-2 btn-integration"
                                    :class="{ 'active': item.id === activeLeafId }"
                                    @click.stop="onLeafClick(item, 'org')">
                                    <v-img
                                        :src="getOrgImage(item.slug)"
                                        :alt="item.name"
                                        width="44"
                                        contain />
                                </v-btn>
                            </div>

                            <!-- Parent node label -->
                            <span
                                v-else
                                class="text-caption text-uppercase">
                                {{ item.name }}
                            </span>
                        </template>
                    </v-treeview>
                </v-list>
            </v-col>
            <v-col
                cols="8"
                class="ml-6">
                <div
                    v-if="integrationDetails?.length"
                    class="details md-typeset"
                    v-html="integrationDetails">
                </div>
                <template v-else-if="selectedOrgIntegration">
                    <v-card class="mx-auto rounded-xxl">
                        <v-card-title class="pa-8">
                            <div class="integration-title-wrap d-flex align-center w-100">
                                <img
                                    class="float-left flex-shrink-0"
                                    height="48"
                                    :src="selectedOrgIntegration.provider?.logoUrl"
                                    :alt="selectedOrgIntegration.provider?.name" />
                                <div class="d-flex flex-column">
                                    <span class="text-heading">
                                        {{ selectedOrgIntegration.provider?.name }}
                                    </span>
                                    <span class="text-body-2 mt-2 word-break-normal">
                                        {{ $t('partnerIntegrations.manageIntegrationInfo', {
                                            integrationName: selectedOrgIntegration.provider?.name,
                                            installationId: selectedOrgIntegration.installationId,
                                            integrationDate:
                                                $moment(selectedOrgIntegration.createdAt).utc().format('ll')
                                        }) }}
                                        <a
                                            :href="selectedOrgIntegration.provider?.documentationUrl"
                                            target="_blank"
                                            rel="noreferrer">
                                            Docs
                                            <v-icon
                                                color="blue"
                                                size="12">
                                                mdi-open-in-new
                                            </v-icon>
                                        </a>
                                    </span>
                                </div>
                                <span
                                    class="align-self-start font-weight-bold ml-auto text-caption flex-shrink-0"
                                    :class="{ 'green--text': selectedOrgIntegration.status?.toLowerCase() === 'active' }">
                                    {{ selectedOrgIntegration.status }}
                                </span>
                            </div>
                        </v-card-title>
                        <v-card-text class="pa-8 pt-0">
                            <div class="mb-2">
                                <span class="font-weight-medium text--disabled">
                                    {{ $t('partnerIntegrations.configurationDetails') }}
                                </span>
                            </div>

                            <div class="list-wrap d-flex flex-column w-100">
                                <div class="list-header d-flex">
                                    <span
                                        v-for="header in configHeaders"
                                        :key="header.text"
                                        class="font-weight-bold px-2 py-1">
                                        {{ header.text }}
                                    </span>
                                </div>
                                <div class="list-row d-flex">
                                    <span class="font-weight-medium px-2 py-1">
                                        {{ selectedOrgIntegration.config?.defaultTeamId }}
                                    </span>
                                    <span class="font-weight-medium px-2 py-1">
                                        {{ $moment(selectedOrgIntegration.config?.createdAt).utc().format('ll') }}
                                    </span>
                                    <span class="font-weight-medium px-2 py-1">
                                        {{ selectedOrgIntegration.config?.name }} ({{ selectedOrgIntegration.config?.username }})
                                    </span>
                                    <span class="font-weight-medium px-2 py-1">
                                        {{ selectedOrgIntegration.config?.email }}
                                    </span>
                                </div>
                            </div>

                            <div class="mt-4 mb-2">
                                <span class="font-weight-medium text--disabled">
                                    {{ $t('partnerIntegrations.linkDetails') }}
                                </span>
                            </div>

                            <v-data-table
                                :headers="headers"
                                :loading="isLinksLoading"
                                :items="selectedIntegrationLinks"
                                item-key="serviceId"
                                class="data-table-full link-table"
                                disable-sort
                                disable-filtering
                                disable-pagination
                                hide-default-footer
                                :no-data-text="noDataText">
                                <template #item="{ item: link }">
                                    <tr>
                                        <td>
                                            <div
                                                v-if="link.service"
                                                class="d-flex flex-column py-2">
                                                <span class="font-weight-bold mb-1">{{ link.service.name }}</span>
                                                <div class="d-flex text-caption">
                                                    <cloud-provider
                                                        :size="18"
                                                        :provider="link.service.provider">
                                                        cloud
                                                    </cloud-provider>
                                                    <span class="ml-1">
                                                        {{ link.topologyName }} | {{ link.service.region }}
                                                    </span>
                                                </div>
                                            </div>
                                            <span v-else>
                                                {{ link.serviceId }}
                                            </span>
                                        </td>
                                        <td>
                                            <div
                                                v-if="link.project"
                                                class="d-flex flex-column py-2">
                                                <span class="font-weight-bold mb-1">{{ link.project.name }}</span>
                                                <span class="text-caption">{{ link.project.id }}</span>
                                            </div>
                                            <span v-else>
                                                {{ link.projectId }}
                                            </span>
                                        </td>
                                        <td>
                                            <div class="d-inline-flex align-center py-2">
                                                <v-btn
                                                    icon
                                                    @click="onClickUnlink">
                                                    <v-icon>mdi-link-off</v-icon>
                                                </v-btn>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </v-data-table>
                        </v-card-text>

                        <v-divider />

                        <v-card-actions class="px-8 py-4">
                            <v-spacer />

                            <v-btn
                                class="px-3"
                                color="error"
                                depressed
                                rounded
                                outlined
                                disabled
                                @click="disconnect">
                                <span class="text-none">
                                    {{ $t('partnerIntegrations.disconnect') }}
                                </span>
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </template>
                <div
                    v-else
                    class="no-selection">
                    <div
                        class="no-selection-content align-center d-flex flex-column justify-center">
                        <v-icon
                            size="48"
                            class="text--disabled">
                            mdi-file-link-outline
                        </v-icon>
                        <span
                            class="text-caption pt-2 text--disabled text-center">
                            {{ $t('partnerIntegrations.selectItemTip') }}
                        </span>
                    </div>
                </div>
            </v-col>
        </v-row>
        <ConfirmationModal
            v-if="orgIntegrations?.length"
            ref="confirmFreeTrialModal"
            title="partnerIntegrations.unlinkConfirmTitle"
            confirm-label="partnerIntegrations.unlink"
            width="800"
            :on-cancel="unlinkCancel"
            :on-confirm="unlinkService">
            <template #content>
                {{ $t('partnerIntegrations.unlinkConfirmMessage') }}
                <v-alert
                    class="mt-4"
                    outlined
                    type="warning">
                    {{ $t('partnerIntegrations.unlinkWarningMessage') }}
                </v-alert>
            </template>
        </ConfirmationModal>
    </div>
</template>
<script>
    import to from 'await-to-js'
    import DOMPurify from 'dompurify'
    import { mapGetters, mapActions } from 'vuex'
    import { getProjects } from 'services/integrations.js'
    import CloudProvider from 'components/service/CloudProvider'
    import ConfirmationModal from 'components/common/ConfirmationModal.vue'
    import { getIntegrations, disconnectService } from 'services/integrations.js'

    export default {
        name: 'IntegrationList',
        components: { CloudProvider, ConfirmationModal, },
        data() {
            return {
                openDoc: [],
                openOrg: [],
                selectedItem: -1,
                vercelProjects: [],
                integrationDetails: '',
                selectedOrgIntegration: null,
                selectedLinkForUnlink: null,
                isDisconnectInProcess: false,
                integrationsTree: null,
                activeLeafId: null,
                docsUrl: 'https://docs.skysql.com',
                orgIntegrations: [],
                noDataText: '$vuetify.noDataText',
                configHeaders: [
                    { text: this.$t('partnerIntegrations.teamId'), },
                    { text: this.$t('partnerIntegrations.creationDate'), },
                    { text: this.$t('partnerIntegrations.nameUsername'), },
                    { text: this.$t('partnerIntegrations.email'), }
                ],
                headers: [
                    { text: this.$t('partnerIntegrations.service'), value: 'service', width: '40%', },
                    { text: this.$t('partnerIntegrations.project'), value: 'project', width: '50%', },
                    { text: '', value: 'action', width: '10%', }
                ],
            }
        },
        computed: {
            ...mapGetters([
                'services',
                'getServiceById'
            ]),
            orgIntegrationsTree() {
                if (!this.orgIntegrations?.length) return []

                const tree = [{
                    id: 101,
                    name: this.$t('partnerIntegrations.orgIntegrations'),
                    link: null,
                    children: this.orgIntegrations.map(integration => ({
                        id: integration.id,
                        name: integration.provider?.name,
                        slug: integration.provider?.slug,
                        link: null,
                        children: undefined,
                    })),
                }]
                return tree
            },
            selectedIntegrationLinks() {
                if (!this.selectedOrgIntegration) return []

                return this.populateLinks()
            },
        },
        methods: {
            ...mapActions(['fetchServices']),
            populateLinks() {
                const links = this.selectedOrgIntegration.data?.filter(
                    dt => dt.externalId === this.selectedOrgIntegration.installationId
                )?.reduce(
                    (acc, current) => acc.concat(current.data?.links || []),
                    []
                )

                links.forEach(link => {
                    link.service = this.getServiceById(link.serviceId)
                    link.project = this.vercelProjects.find(project => project.id === link.projectId)
                    link.topologyName = this.$help.getTopologyName(link.service.topology, true)
                })

                return links
            },
            async extractIntegrationsHierarchy(docsUrl) {
                const html = await fetch(docsUrl).then(res => res.text())
                const parser = new DOMParser()
                const doc = parser.parseFromString(html, 'text/html')

                let idCounter = 1

                const navList = doc.querySelector('ul.md-nav__list')
                if (!navList) throw new Error('Navigation list not found')

                // Helper to recursively parse <li> items
                function parseListItem(li) {
                    let linkEl = li.querySelector(':scope > a, :scope > span') // supports both linkable and non-linkable items
                    if (!linkEl) {
                        linkEl = li.querySelector(':scope > .md-nav__link > a, :scope > .md-nav__link > span')
                    }
                    const name = linkEl?.textContent.trim() || 'Untitled'
                    const link = linkEl?.tagName === 'A' ? linkEl.getAttribute('href') : null

                    const childrenList = li.querySelector(':scope ul.md-nav__list')
                    const children = []

                    if (childrenList) {
                        childrenList.querySelectorAll(':scope > li').forEach(childLi => {
                            children.push(parseListItem(childLi))
                        })
                    }

                    return {
                        id: idCounter++,
                        name,
                        link,
                        children: children.length ? children : undefined,
                    }
                }

                // Find the <li> whose label is "Integrations"
                const allNavItems = navList.querySelectorAll('li')
                let integrationsItem = null

                allNavItems.forEach(li => {
                    const text = li.querySelector(':scope > .md-nav__link > a, :scope > .md-nav__link > span')?.textContent?.trim()
                    if (text === 'Integrations' && !integrationsItem) {
                        integrationsItem = li
                    }
                })

                if (!integrationsItem) throw new Error('"Integrations" not found')

                return [parseListItem(integrationsItem)]
            },
            loadIntegration(url) {
                fetch(url)
                    .then(res => res.text())
                    .then(html => {
                        const parser = new DOMParser()
                        const doc = parser.parseFromString(html, 'text/html')
                        const article = doc.querySelector('article.md-content__inner')
                        this.integrationDetails = article ?
                            DOMPurify.sanitize(article.innerHTML) :
                            this.$t('partnerIntegrations.noContent')
                    })
                    .catch(() => {
                        this.integrationDetails = this.$t('partnerIntegrations.failedFetchTip')
                    })
            },
            async fetchIntegrationsHierarchy() {
                try {
                    const tree = await this.extractIntegrationsHierarchy(this.docsUrl)
                    this.integrationsTree = tree?.[0]?.children || []
                } catch (error) {
                    this.integrationsTree = null
                }
            },
            onItemClick() {
                // No-op — visual selection not needed
            },
            onLeafClick(item, type = 'doc') {
                this.activeLeafId = item.id

                this.integrationDetails = ''
                this.selectedOrgIntegration = null

                switch (type) {
                    case 'org': {
                        this.selectedOrgIntegration = this.orgIntegrations.filter(integration => integration.id === item.id)?.[0]
                        break
                    }
                    default: {
                        if (item.link) {
                            this.loadIntegration(`${this.docsUrl}/${item.link}`)
                        }
                    }
                }
            },
            findItemById(items, id) {
                for (let item of items) {
                    if (item.id === id) return item
                    if (item.children) {
                        const found = this.findItemById(item.children, id)
                        if (found) return found
                    }
                }
                return null
            },
            getLogoUrl(name) {
                const slug = name.toLowerCase().replace(/\s+/g, '-')
                return `${this.docsUrl}/assets/integrations/${slug}.png`
            },
            getOrgImage(slug = '') {
                try {
                    return require(`@/assets/org-logos/${slug.replace(/\s+/g, '-')}.svg`)
                } catch (e) {
                    return require('@/assets/org-logos/default.png')
                }
            },
            onClickUnlink(link) {
                if (link && this.$refs.confirmFreeTrialModal) {
                    this.selectedLinkForUnlink = link
                    this.$refs.confirmFreeTrialModal.open()
                }
            },
            unlinkCancel() {
                this.selectedLinkForUnlink = null
            },
            async unlinkService() {
                if (!this.selectedLinkForUnlink) return
                this.isDisconnectInProcess = true
                try {
                    // eslint-disable-next-line no-unused-vars
                    const [error, response] = await to(disconnectService({
                        serviceId: this.selectedLinkForUnlink.serviceId,
                        projectId: this.selectedLinkForUnlink.projectId,
                    }))
                    if (error) {
                        this.$store.commit('showMessage', { text: this.$t('errors.unknown'), type: this.$config.messageType.ERROR, })
                    } else {
                        this.$store.commit('showMessage', { text: this.$t('success.generic'), type: this.$config.messageType.SUCCESS, })
                        this.fetchOrgIntegrations()
                    }
                } catch (error) {
                    this.$store.commit('showMessage', { text: this.$t('errors.unknown'), type: this.$config.messageType.ERROR, })
                } finally {
                    this.selectedLinkForUnlink = null
                    this.isDisconnectInProcess = false
                }
            },
            disconnect() {},
            async fetchOrgIntegrations() {
                try {
                    const [error, response] = await to(getIntegrations())
                    if (error) {
                        this.$store.commit('showMessage', { text: this.$t('errors.unknown'), type: this.$config.messageType.ERROR, })
                    } else {
                        this.orgIntegrations = response || []
                    }
                } catch (error) {
                    this.$store.commit('showMessage', { text: this.$t('errors.unknown'), type: this.$config.messageType.ERROR, })
                }

                try {
                    if (localStorage?.getItem('__IS_FOR_VERCEL_CONFIG__') !== 'true') return
                    const searchParams = localStorage?.getItem('__VERCEL_CONFIG_SEARCH__')
                    if (!searchParams?.length) return
                    const params = new URLSearchParams(searchParams)
                    const installationId = params.get('configurationId')

                    if (!installationId?.length) return

                    const integration = this.orgIntegrations.filter(integration => integration.installationId === installationId)?.[0]
                    if (!integration) return

                    setTimeout(() => {
                        this.onLeafClick(integration, 'org')
                    }, 100)
                } catch (error) {
                    //
                } finally {
                    localStorage?.removeItem('__IS_FOR_VERCEL_CONFIG__')
                    localStorage?.removeItem('__VERCEL_CONFIG_SEARCH__')
                }
            },
        },
        async mounted() {
            if (!this.services?.length) {
                this.fetchServices()
            }
            this.fetchIntegrationsHierarchy()
            this.fetchOrgIntegrations()
            // eslint-disable-next-line no-unused-vars
            const [error, response] = await to(getProjects())
            if (response) {
                this.vercelProjects = response
            }
        },
    }
</script>
